import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, Paper, Stack, CircularProgress,
  IconButton, Tooltip, ToggleButton, ToggleButtonGroup, Snackbar, Alert,
} from "@mui/material";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import RefreshIcon from "@mui/icons-material/Refresh";
import GetAppIcon from "@mui/icons-material/GetApp";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import StorageIcon from "@mui/icons-material/Storage";
import TextField from "@mui/material/TextField";
import { runMultiQuery, runNLQuery } from "../api/oracleApi";

const PIE_C = ["#2563eb","#7c3aed","#0891b2","#059669","#d97706","#dc2626","#4f46e5","#db2777"];

const fmtVal = (v, kind) => {
  if (v == null || typeof v !== "number") return v ?? "—";
  if (kind === "currency") {
    if (Math.abs(v) >= 1e6) return "$" + (v/1e6).toFixed(2) + "M";
    if (Math.abs(v) >= 1e3) return "$" + (v/1e3).toFixed(1) + "K";
    return "$" + v.toLocaleString(undefined,{maximumFractionDigits:0});
  }
  if (Math.abs(v) >= 1e6) return (v/1e6).toFixed(2) + "M";
  if (Math.abs(v) >= 1e3) return (v/1e3).toFixed(1) + "K";
  return v.toLocaleString(undefined,{maximumFractionDigits:2});
};
const truncate = (s,n=16) => (s && String(s).length>n ? String(s).slice(0,n)+"…" : s);

// ===========================================================================
// SUBJECT AREAS — one per schema/pillar. Each chart is a verified measure+dim.
// ===========================================================================
const AREAS = [
  {
    key:"sales", label:"Sales", schema:"AIDATALOOKS_DW", color:"#2563eb",
    subtitle:"Orders fact joined to product, region & sales-rep dimensions",
    kpiFmt:"currency", kpiTitle:"Total Sales",
    charts:[
      { title:"Sales by Product Type", type:"bar",   fact:"D1_ORDERS2", agg:"SUM",
        measure:{table:"D1_ORDERS2",column:"ACTLEXTND"}, dim:{table:"D1_PRODUCT_TYPE",column:"ITEMTYPE"} },
      { title:"Sales by Region", type:"pie", fact:"D1_ORDERS2", agg:"SUM",
        measure:{table:"D1_ORDERS2",column:"ACTLEXTND"}, dim:{table:"D1_REGION",column:"REGION"} },
      { title:"Units Shipped by Subtype", type:"barh", fact:"D1_ORDERS2", agg:"SUM",
        measure:{table:"D1_ORDERS2",column:"UNITSHPD"}, dim:{table:"D1_PRODUCT_SUBTYPE",column:"ITEMSUBTYPE"} },
      { title:"Sales by Sales Rep", type:"table", fact:"D1_ORDERS2", agg:"SUM",
        measure:{table:"D1_ORDERS2",column:"ACTLEXTND"}, dim:{table:"D1_SALESREPS",column:"SALESREP"} },
    ],
  },
  {
    key:"receivables", label:"Receivables (ERP)", schema:"AIDATALOOKS_ERP", color:"#0891b2",
    subtitle:"Accounts Receivable transactions across business units",
    kpiFmt:"currency", kpiTitle:"Total Receivables",
    charts:[
      { title:"Revenue by Customer Segment", type:"bar", fact:"DW_AR_TRANSACTION_LINE_F", agg:"SUM",
        measure:{table:"DW_AR_TRANSACTION_LINE_F",column:"TRANSACTION_AMOUNT"}, dim:{table:"DW_CUSTOMER_D",column:"CUSTOMER_SEGMENT"} },
      { title:"Outstanding by Business Unit", type:"pie", fact:"DW_AR_TRANSACTION_LINE_F", agg:"SUM",
        measure:{table:"DW_AR_TRANSACTION_LINE_F",column:"OUTSTANDING_AMOUNT"}, dim:{table:"DW_BUSINESS_UNIT_D",column:"BUSINESS_UNIT_NAME"} },
      { title:"Revenue by Account Type", type:"barh", fact:"DW_AR_TRANSACTION_LINE_F", agg:"SUM",
        measure:{table:"DW_AR_TRANSACTION_LINE_F",column:"TRANSACTION_AMOUNT"}, dim:{table:"DW_GL_ACCOUNT_D",column:"ACCOUNT_TYPE"} },
      { title:"Top Customers by Revenue", type:"table", fact:"DW_AR_TRANSACTION_LINE_F", agg:"SUM",
        measure:{table:"DW_AR_TRANSACTION_LINE_F",column:"TRANSACTION_AMOUNT"}, dim:{table:"DW_CUSTOMER_D",column:"CUSTOMER_NAME"} },
    ],
  },
  {
    key:"workforce", label:"Workforce (HCM)", schema:"AIDATALOOKS_HCM", color:"#7c3aed",
    subtitle:"Worker assignments by department, job & location",
    kpiFmt:"number", kpiTitle:"Total Headcount",
    charts:[
      { title:"Headcount by Department", type:"bar", fact:"DW_WORKER_ASSIGNMENT_F", agg:"SUM",
        measure:{table:"DW_WORKER_ASSIGNMENT_F",column:"HEADCOUNT"}, dim:{table:"DW_DEPARTMENT_D",column:"DEPARTMENT_NAME"} },
      { title:"Headcount by Location", type:"pie", fact:"DW_WORKER_ASSIGNMENT_F", agg:"SUM",
        measure:{table:"DW_WORKER_ASSIGNMENT_F",column:"HEADCOUNT"}, dim:{table:"DW_LOCATION_D",column:"LOCATION_NAME"} },
      { title:"Avg Salary by Job", type:"barh", fact:"DW_WORKER_ASSIGNMENT_F", agg:"AVG",
        measure:{table:"DW_WORKER_ASSIGNMENT_F",column:"ANNUAL_SALARY"}, dim:{table:"DW_JOB_D",column:"JOB_NAME"} },
      { title:"Avg Salary by Department", type:"table", fact:"DW_WORKER_ASSIGNMENT_F", agg:"AVG",
        measure:{table:"DW_WORKER_ASSIGNMENT_F",column:"ANNUAL_SALARY"}, dim:{table:"DW_DEPARTMENT_D",column:"DEPARTMENT_NAME"} },
    ],
  },
  {
    key:"supplychain", label:"Supply Chain (SCM)", schema:"AIDATALOOKS_SCM", color:"#059669",
    subtitle:"Sales orders & inventory across orgs and products",
    kpiFmt:"currency", kpiTitle:"Total Order Value",
    charts:[
      { title:"Order Value by Product Category", type:"bar", fact:"DW_SALES_ORDER_LINE_F", agg:"SUM",
        measure:{table:"DW_SALES_ORDER_LINE_F",column:"ORDER_AMOUNT"}, dim:{table:"DW_PRODUCT_D",column:"PRODUCT_CATEGORY"} },
      { title:"Orders by Inventory Org", type:"pie", fact:"DW_SALES_ORDER_LINE_F", agg:"SUM",
        measure:{table:"DW_SALES_ORDER_LINE_F",column:"ORDER_AMOUNT"}, dim:{table:"DW_INVENTORY_ORG_D",column:"INVENTORY_ORG_NAME"} },
      { title:"Orders by Customer Segment", type:"barh", fact:"DW_SALES_ORDER_LINE_F", agg:"SUM",
        measure:{table:"DW_SALES_ORDER_LINE_F",column:"ORDER_AMOUNT"}, dim:{table:"DW_CUSTOMER_D",column:"CUSTOMER_SEGMENT"} },
      { title:"Order Value by Business Unit", type:"table", fact:"DW_SALES_ORDER_LINE_F", agg:"SUM",
        measure:{table:"DW_SALES_ORDER_LINE_F",column:"ORDER_AMOUNT"}, dim:{table:"DW_BUSINESS_UNIT_D",column:"BUSINESS_UNIT_NAME"} },
    ],
  },
  {
    key:"service", label:"Service (CX)", schema:"AIDATALOOKS_CX", color:"#d97706",
    subtitle:"Service requests, satisfaction & resolution time",
    kpiFmt:"number", kpiTitle:"Avg Satisfaction",
    kpiReduce:"avg",
    charts:[
      { title:"Avg Satisfaction by Severity", type:"bar", fact:"DW_SERVICE_REQUEST_F", agg:"AVG",
        measure:{table:"DW_SERVICE_REQUEST_F",column:"SATISFACTION_SCORE"}, dim:{table:"DW_SERVICE_REQUEST_F",column:"SR_SEVERITY"} },
      { title:"Requests Satisfaction by Channel", type:"pie", fact:"DW_SERVICE_REQUEST_F", agg:"AVG",
        measure:{table:"DW_SERVICE_REQUEST_F",column:"SATISFACTION_SCORE"}, dim:{table:"DW_CHANNEL_D",column:"CHANNEL_NAME"} },
      { title:"Avg Resolution Hours by Status", type:"barh", fact:"DW_SERVICE_REQUEST_F", agg:"AVG",
        measure:{table:"DW_SERVICE_REQUEST_F",column:"RESOLUTION_TIME_HOURS"}, dim:{table:"DW_SR_STATUS_D",column:"STATUS_NAME"} },
      { title:"Satisfaction by Product Category", type:"table", fact:"DW_SERVICE_REQUEST_F", agg:"AVG",
        measure:{table:"DW_SERVICE_REQUEST_F",column:"SATISFACTION_SCORE"}, dim:{table:"DW_PRODUCT_D",column:"PRODUCT_CATEGORY"} },
    ],
  },
];

function KPICard({ title, value, fmt, color }) {
  return (
    <Paper elevation={2} sx={{ p:2.5, flex:"1 1 190px", borderRadius:2.5, borderTop:`4px solid ${color}`, bgcolor:"#fff" }}>
      <Typography sx={{ fontSize:10.5, fontWeight:700, color:"#888", letterSpacing:1, textTransform:"uppercase", mb:0.5 }}>{title}</Typography>
      <Typography sx={{ fontSize:26, fontWeight:800, color, my:0.5, lineHeight:1.15 }}>{fmtVal(value, fmt)}</Typography>
      <Typography sx={{ fontSize:11, color:"#22c55e", fontWeight:600 }}>● Live Oracle data</Typography>
    </Paper>
  );
}

function ChartCard({ spec, data, loading, color }) {
  const fmtAxis = v => fmtVal(v, spec.fact && /AMOUNT|SALES|VALUE|ACTL/.test(spec.measure.column) ? "currency" : "number");
  return (
    <Paper elevation={1} sx={{ p:2, borderRadius:2, bgcolor:"#fff" }}>
      <Typography sx={{ fontWeight:700, fontSize:13, color:"#222", mb:0.25 }}>{spec.title}</Typography>
      <Typography sx={{ fontSize:10.5, color:"#aaa", mb:1 }}>{spec.agg}({spec.measure.column}) · {spec.dim.table}.{spec.dim.column}</Typography>
      {loading ? (
        <Box sx={{ height:240, display:"flex", alignItems:"center", justifyContent:"center" }}><CircularProgress size={26}/></Box>
      ) : data.length === 0 ? (
        <Box sx={{ height:240, display:"flex", alignItems:"center", justifyContent:"center", color:"#bbb" }}>No data</Box>
      ) : spec.type === "bar" ? (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} margin={{ top:5,right:10,bottom:60,left:5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
            <XAxis dataKey="label" tick={{ fontSize:10 }} angle={-35} textAnchor="end" height={70} tickFormatter={v=>truncate(v,12)}/>
            <YAxis tick={{ fontSize:10 }} tickFormatter={fmtAxis}/>
            <RTooltip formatter={v=>fmtAxis(v)}/>
            <Bar dataKey="value" fill={color} radius={[3,3,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      ) : spec.type === "pie" ? (
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="46%" outerRadius={90} innerRadius={42}
              label={({name,percent})=>`${truncate(name,10)} ${(percent*100).toFixed(0)}%`}>
              {data.map((_,i)=><Cell key={i} fill={PIE_C[i%PIE_C.length]}/>)}
            </Pie>
            <RTooltip formatter={v=>fmtAxis(v)}/>
            <Legend iconSize={9} wrapperStyle={{ fontSize:10 }}/>
          </PieChart>
        </ResponsiveContainer>
      ) : spec.type === "barh" ? (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} layout="vertical" margin={{ top:5,right:40,bottom:5,left:80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
            <XAxis type="number" tick={{ fontSize:10 }} tickFormatter={fmtAxis}/>
            <YAxis dataKey="label" type="category" width={80} tick={{ fontSize:10 }} tickFormatter={v=>truncate(v,12)}/>
            <RTooltip formatter={v=>fmtAxis(v)}/>
            <Bar dataKey="value" fill={color} radius={[0,3,3,0]}/>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{ overflow:"auto", maxHeight:280 }}>
          <table style={{ borderCollapse:"collapse", width:"100%", fontSize:12 }}>
            <thead><tr>{["#","Category","Value"].map(h=>(
              <th key={h} style={{ padding:"6px 10px", background:"#37474f", color:"#fff", textAlign:h==="Value"?"right":"left", position:"sticky", top:0 }}>{h}</th>
            ))}</tr></thead>
            <tbody>{data.map((r,i)=>(
              <tr key={i}>
                <td style={{ padding:"5px 10px", background:i%2?"#fff":"#f8f9fa", color:"#999", borderBottom:"1px solid #eef" }}>{i+1}</td>
                <td style={{ padding:"5px 10px", background:i%2?"#fff":"#f8f9fa", borderBottom:"1px solid #eef" }}>{r.label}</td>
                <td style={{ padding:"5px 10px", background:i%2?"#fff":"#f8f9fa", textAlign:"right", fontWeight:600, color, borderBottom:"1px solid #eef", fontFamily:"monospace" }}>{fmtAxis(r.value)}</td>
              </tr>
            ))}</tbody>
          </table>
        </Box>
      )}
    </Paper>
  );
}

function AIPanel() {
  const [q,setQ]=useState(""); const [loading,setLoading]=useState(false);
  const [result,setResult]=useState(null); const [err,setErr]=useState("");
  const run=async()=>{ if(!q.trim())return; setLoading(true); setErr(""); setResult(null);
    try{ setResult(await runNLQuery(q.trim())); }catch(e){ setErr(e.message); } setLoading(false); };
  const cols=result?.columns||[], data=result?.data||[];
  return (
    <Paper elevation={1} sx={{ p:2, borderRadius:2, bgcolor:"#fffde7", border:"1px solid #fde68a" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb:1.5 }}>
        <AutoFixHighIcon sx={{ color:"#f57f17" }}/>
        <Typography sx={{ fontWeight:700, color:"#92400e", fontSize:13 }}>Ask AI (Groq LLaMA)</Typography>
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField size="small" placeholder='e.g. "Top 10 products by sales"' value={q} fullWidth
          onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&run()} sx={{ bgcolor:"#fff" }}/>
        <Button variant="contained" disabled={!q.trim()||loading} onClick={run}
          sx={{ bgcolor:"#f57f17","&:hover":{bgcolor:"#e65100"}, minWidth:110 }}
          startIcon={loading?<CircularProgress size={14} color="inherit"/>:<AutoFixHighIcon/>}>
          {loading?"Thinking…":"Run AI"}
        </Button>
      </Stack>
      {err && <Alert severity="error" sx={{ mt:1, fontSize:12 }}>{err}</Alert>}
      {result && (
        <Box sx={{ mt:1.5, overflow:"auto", maxHeight:260 }}>
          <table style={{ borderCollapse:"collapse", width:"100%", fontSize:12 }}>
            <thead><tr>{cols.map(c=><th key={c} style={{ padding:"5px 10px", background:"#37474f", color:"#fff", textAlign:"left" }}>{c}</th>)}</tr></thead>
            <tbody>{data.slice(0,25).map((row,i)=>(
              <tr key={i}>{cols.map(c=><td key={c} style={{ padding:"4px 10px", background:i%2?"#fff":"#f8f9fa", fontFamily:"monospace", textAlign:typeof row[c]==="number"?"right":"left" }}>{typeof row[c]==="number"?row[c].toLocaleString():String(row[c]??"")}</td>)}</tr>
            ))}</tbody>
          </table>
        </Box>
      )}
    </Paper>
  );
}

// ===========================================================================
export default function Dashboard() {
  const [areaKey, setAreaKey] = useState("sales");
  const area = AREAS.find(a => a.key === areaKey);
  const [chartData, setChartData] = useState([[], [], [], []]);
  const [loading, setLoading] = useState([true,true,true,true]);
  const [err, setErr] = useState("");

  const loadArea = useCallback(async (a) => {
    setLoading([true,true,true,true]);
    a.charts.forEach(async (spec, idx) => {
      try {
        const d = await runMultiQuery({
          factTable: spec.fact, measure: spec.measure, dimensions:[spec.dim],
          agg: spec.agg, limit:15, filters:[],
        });
        setChartData(prev => { const n=[...prev]; n[idx]=d.data||[]; return n; });
      } catch(e){ setErr(e.message); setChartData(prev=>{ const n=[...prev]; n[idx]=[]; return n; }); }
      setLoading(prev => { const n=[...prev]; n[idx]=false; return n; });
    });
  }, []);

  useEffect(() => {
    loadArea(area);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaKey]);

  // headline KPI from chart[0]
  const kpiVal = (() => {
    const d = chartData[0] || [];
    if (!d.length) return null;
    const sum = d.reduce((s,r)=>s+(r.value||0),0);
    return area.kpiReduce === "avg" ? sum/d.length : sum;
  })();
  const cats = (chartData[0]||[]).length;
  const topVal = Math.max(0, ...(chartData[0]||[]).map(r=>r.value||0));

  const exportArea = () => {
    const rows = area.charts.flatMap((s,i)=>(chartData[i]||[]).map(r=>`"${s.title}","${r.label}",${r.value}`));
    const csv = ["Chart,Category,Value", ...rows].join("\n");
    const a=document.createElement("a"); a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
    a.download=`${area.key}_dashboard.csv`; a.click();
  };

  return (
    <Box sx={{ minHeight:"100vh", bgcolor:"#eef0f5", display:"flex", flexDirection:"column" }}>
      {/* Header */}
      <Paper elevation={0} sx={{ bgcolor:"#fff", px:3, py:2, borderBottom:"1px solid #d0d7e0" }}>
        <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight:700, color:"#1a1a1a" }}>Enterprise Analytics Dashboard</Typography>
            <Typography sx={{ fontSize:12, color:"#888" }}>
              <StorageIcon sx={{ fontSize:13, verticalAlign:"middle", mr:0.5, color:area.color }}/>
              {area.schema} · {area.subtitle}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh"><IconButton size="small" onClick={()=>loadArea(area)}><RefreshIcon/></IconButton></Tooltip>
            <Button variant="outlined" startIcon={<GetAppIcon/>} onClick={exportArea} size="small">Export</Button>
          </Stack>
        </Box>
      </Paper>

      {/* Subject-area switcher */}
      <Paper elevation={0} sx={{ bgcolor:"#fff", px:3, py:1.5, borderBottom:"1px solid #d0d7e0" }}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight:700, fontSize:12, color:"#666" }}>SUBJECT AREA</Typography>
          <ToggleButtonGroup exclusive size="small" value={areaKey}
            onChange={(e,v)=>v&&setAreaKey(v)}>
            {AREAS.map(a => (
              <ToggleButton key={a.key} value={a.key}
                sx={{ textTransform:"none", fontWeight:600, fontSize:12.5, px:2,
                      "&.Mui-selected":{ bgcolor:a.color, color:"#fff", "&:hover":{ bgcolor:a.color } } }}>
                {a.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      <Box sx={{ flex:1, p:2.5, display:"flex", flexDirection:"column", gap:2.5, overflow:"auto" }}>
        {/* KPIs */}
        <Box sx={{ display:"flex", gap:2, flexWrap:"wrap" }}>
          <KPICard title={area.kpiTitle} value={kpiVal} fmt={area.kpiReduce==="avg"?"number":area.kpiFmt} color={area.color}/>
          <KPICard title="Categories" value={cats} fmt="number" color={area.color}/>
          <KPICard title={area.kpiReduce==="avg"?"Highest avg":"Top value"} value={topVal} fmt={area.kpiReduce==="avg"?"number":area.kpiFmt} color={area.color}/>
        </Box>

        {/* 2x2 charts */}
        <Box sx={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:2 }}>
          {area.charts.map((spec, i) => (
            <ChartCard key={`${areaKey}-${i}`} spec={spec} data={chartData[i]||[]} loading={loading[i]} color={area.color}/>
          ))}
        </Box>

        <AIPanel/>
      </Box>

      <Snackbar open={!!err} autoHideDuration={5000} onClose={()=>setErr("")}
        anchorOrigin={{ vertical:"bottom", horizontal:"center" }}>
        <Alert severity="error" onClose={()=>setErr("")}>{err}</Alert>
      </Snackbar>
    </Box>
  );
}
